import { isEmpty } from '@utils/checkers'

export type Folder = {
  id: number
  name: string
  parent_id: number
  parent_uuid: string
  uuid: string
  openChildren?: boolean
  items?: Folder[]
  isActive?: boolean
}

type Config = {
  openChildren: boolean
}

type Tree = {
  [key: number]: Folder
}

type ParentIdTree = {
  [key: number]: Folder[]
}

/**
 * receives an array of objects to generate a tree
 *
 * @function generateTree
 * @param    {Array} folders
 * @param    {Config} config
 * @returns  {Array}
 */
export const generateTree = (
  folders: Folder[],
  config: Config = { openChildren: false },
): Tree => {
  const tree = {} as ParentIdTree

  folders.forEach((folder: Folder) => {
    const parentId = folder.parent_id || 0

    const newFolder: Folder = { ...folder }
    newFolder.openChildren = !!config?.openChildren

    if (typeof tree[parentId] === 'undefined') {
      tree[parentId] = []
    }

    tree[parentId].push(newFolder)
  })

  return generateTreeItems(0, tree)
}

/**
 * receives an array to generate a nested array of objects
 * based on specific keys
 *
 * @function generateTreeItems
 * @param    {Number} parentId
 * @param    {Array} items
 * @returns  {Array}
 */

export const generateTreeItems = (
  parentId: number,
  items: ParentIdTree,
): Folder[] => {
  const tree = {} as Tree

  if (items[parentId]) {
    const result = items[parentId]

    result.forEach((cat: Folder) => {
      if (!tree[cat.id]) {
        tree[cat.id] = { ...cat, items: [] }
      }

      tree[cat.id] = { ...cat }
      tree[cat.id].items = generateTreeItems(cat.id, items)
    })
  }

  return sortAlphabetically(
    Object.keys(tree).map((key: string) => tree[key]),
    'name',
  )
}

/**
 * sort array alphabetically
 * based on specific key
 *
 * @function sortAlphabetically
 * @param    {Array} array
 * @param    {String} key
 * @returns  {Array}
 */

export const sortAlphabetically = (
  array: unknown[],
  key: string,
): unknown[] => {
  return array.sort((a, b) => {
    if (a.id === null) {
      return -1
    } // if id is null set item as first
    const as = a[key] ? a[key].toLowerCase() : a[key]
    const bs = b[key] ? b[key].toLowerCase() : b[key]
    if (as < bs) {
      return -1
    }
    if (as > bs) {
      return 1
    }
    return 0
  })
}

/**
 * isAssetFolderChildOf returns if a folder is child of a specific folder
 *
 * @function isAssetFolderChildOf
 * @param    {Folder} folder
 * @param    {Number|String} targetFolderId
 * @returns  {boolean}
 */

export const isAssetFolderChildOf = (
  folder: Folder,
  targetFolderId: number,
): boolean => {
  let childFound = false

  if (isEmpty(folder) && !folder.items) {
    return childFound
  }

  folder.items!.some((subFolder: Folder) => {
    if (subFolder.id === targetFolderId) {
      childFound = true
    } else if (subFolder.items && subFolder.items.length > 0) {
      childFound = isAssetFolderChildOf(subFolder, targetFolderId)
    } else {
      childFound = false
    }
    return childFound
  })

  return childFound
}

/**
 * getRootFolder returns if a folder is root given an array of folders
 *
 * @function getRootFolder
 * @param    {Number|String} folderId
 * @param    {Array} folders
 * @returns  {Object|Null}
 */
export const getRootFolder = (
  folderId: number,
  folders: Folder[],
): Folder | null => {
  let rootFolder = null

  folders.some((folder: Folder): boolean | undefined => {
    if (isAssetFolderChildOf(folder, folderId)) {
      rootFolder = folder
      return true
    }
  })

  return rootFolder
}
